import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Bold, Italic, Heading1, Heading2, List, Link as LinkIcon } from "lucide-react";
import { getAllTerms, updateTerms } from "@/lib/api";
import { renderMarkdown } from "@/lib/markdown";
import { testIds } from "@/constants/testIds/b2b";

const KINDS = [
  { id: "prebook", label: "Prebook" },
  { id: "in_stock", label: "In-Stock" },
];

export default function TermsEditor() {
  const [activeKind, setActiveKind] = useState("prebook");
  const [byKind, setByKind] = useState({
    prebook: { text: "", version: 1, updated_at: null },
    in_stock: { text: "", version: 1, updated_at: null },
  });
  const [forceResign, setForceResign] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const textareaRef = useRef(null);

  const current = byKind[activeKind];
  const text = current.text;
  const setText = (next) =>
    setByKind((s) => ({ ...s, [activeKind]: { ...s[activeKind], text: next } }));

  useEffect(() => {
    (async () => {
      try {
        const all = await getAllTerms();
        setByKind({
          prebook: {
            text: all?.prebook?.text || "",
            version: all?.prebook?.version || 1,
            updated_at: all?.prebook?.updated_at || null,
          },
          in_stock: {
            text: all?.in_stock?.text || "",
            version: all?.in_stock?.version || 1,
            updated_at: all?.in_stock?.updated_at || null,
          },
        });
      } catch (e) {
        toast.error("Failed to load terms");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const wrapSelection = (before, after = before, placeholder = "") => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const selected = text.slice(start, end) || placeholder;
    const next = text.slice(0, start) + before + selected + after + text.slice(end);
    setText(next);
    requestAnimationFrame(() => {
      ta.focus();
      const pos = start + before.length + selected.length;
      ta.setSelectionRange(pos, pos);
    });
  };

  const prefixLine = (prefix) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const lineStart = text.lastIndexOf("\n", start - 1) + 1;
    const before = text.slice(0, lineStart);
    const after = text.slice(lineStart);
    setText(before + prefix + after);
    requestAnimationFrame(() => {
      ta.focus();
      const pos = start + prefix.length;
      ta.setSelectionRange(pos, pos);
    });
  };

  const insertLink = () => {
    const url = window.prompt("Enter URL", "https://");
    if (!url) return;
    wrapSelection("[", `](${url})`, "link text");
  };

  const onSave = async () => {
    setSaving(true);
    try {
      const result = await updateTerms(activeKind, { text, force_resign: forceResign });
      setByKind((s) => ({
        ...s,
        [activeKind]: { text: result.text, version: result.version, updated_at: result.updated_at },
      }));
      const versionBumped = forceResign;
      setForceResign(false);
      const label = KINDS.find((k) => k.id === activeKind)?.label || activeKind;
      toast.success(
        versionBumped
          ? `${label} terms saved · version bumped to ${result.version}. Buyers will be asked to re-accept.`
          : `${label} terms saved`,
      );
    } catch (e) {
      toast.error("Failed to save terms");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-sm text-muted-foreground py-12">Loading terms…</div>;
  }

  return (
    <div data-testid={testIds.termsEditor} className="space-y-6 max-w-5xl">
      {/* Kind tabs */}
      <div className="inline-flex rounded-full border border-border bg-background p-1">
        {KINDS.map((k) => (
          <button
            key={k.id}
            type="button"
            onClick={() => { setActiveKind(k.id); setForceResign(false); }}
            data-testid={`terms-tab-${k.id}`}
            className={`px-4 py-1.5 text-xs uppercase tracking-[0.18em] rounded-full transition-colors ${
              activeKind === k.id
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {k.label}
          </button>
        ))}
      </div>

      <div className="flex items-center justify-between text-xs uppercase tracking-[0.18em] text-muted-foreground">
        <span>Version {current.version}</span>
        {current.updated_at && <span>Updated {new Date(current.updated_at).toLocaleString()}</span>}
      </div>

      {/* Toolbar */}
      <div className="inline-flex items-center gap-1 p-1 rounded-full border border-border bg-background">
        <ToolBtn testid={testIds.termsToolbarBold} icon={Bold} label="Bold" onClick={() => wrapSelection("**", "**", "bold text")} />
        <ToolBtn testid={testIds.termsToolbarItalic} icon={Italic} label="Italic" onClick={() => wrapSelection("_", "_", "italic text")} />
        <ToolBtn testid={testIds.termsToolbarH1} icon={Heading1} label="H1" onClick={() => prefixLine("# ")} />
        <ToolBtn testid={testIds.termsToolbarH2} icon={Heading2} label="H2" onClick={() => prefixLine("## ")} />
        <ToolBtn testid={testIds.termsToolbarList} icon={List} label="List" onClick={() => prefixLine("- ")} />
        <ToolBtn testid={testIds.termsToolbarLink} icon={LinkIcon} label="Link" onClick={insertLink} />
      </div>

      {/* Editor + Preview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground block mb-2">Markdown</label>
          <textarea
            ref={textareaRef}
            data-testid={testIds.termsTextarea}
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={20}
            spellCheck={false}
            className="w-full px-4 py-3 rounded-2xl border border-border bg-transparent focus:outline-none focus:border-primary font-mono text-sm leading-relaxed"
            placeholder={`# ${activeKind === "prebook" ? "Prebook" : "In-Stock"} Terms\n\nWrite your terms in markdown...`}
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground block mb-2">Live preview</label>
          <div
            data-testid={testIds.termsPreview}
            className="prose-terms w-full min-h-[28rem] px-5 py-4 rounded-2xl border border-border bg-[hsl(var(--surface))]/40 overflow-auto text-sm leading-relaxed"
            dangerouslySetInnerHTML={{ __html: renderMarkdown(text) }}
          />
        </div>
      </div>

      {/* Force re-sign + Save */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-2 border-t border-border">
        <label className="flex items-center gap-3 cursor-pointer text-sm">
          <input
            data-testid={testIds.termsForceResign}
            type="checkbox"
            checked={forceResign}
            onChange={(e) => setForceResign(e.target.checked)}
            className="w-4 h-4 accent-[hsl(var(--primary))]"
          />
          <span>
            Force buyers to re-sign{" "}
            <span className="text-muted-foreground">(bumps version for this kind)</span>
          </span>
        </label>
        <button
          type="button"
          data-testid={testIds.termsSaveBtn}
          onClick={onSave}
          disabled={saving}
          className="px-6 py-3 rounded-full bg-primary text-primary-foreground disabled:opacity-60 hover:-translate-y-0.5 transition-transform shadow-soft"
        >
          {saving ? "Saving…" : `Save ${KINDS.find((k) => k.id === activeKind)?.label || ""} terms`}
        </button>
      </div>
    </div>
  );
}

function ToolBtn({ icon: Icon, label, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      title={label}
      aria-label={label}
      className="p-2 rounded-full hover:bg-foreground/5 text-foreground/70 hover:text-foreground"
    >
      <Icon size={15} strokeWidth={1.5} />
    </button>
  );
}
